import { Draft, produce } from 'immer';
import { set } from 'lodash';

import { SaveInternalBannersSuccess } from '../../../actions/bannersEdit/internal/saveInternalBannersSuccess';
import { internalBannerFieldErrorMapper } from '../../../service/BannerEditErrors/BannerEditErrors';
import { internalBannersErrorsMapper } from '../../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { buildErrorTree, convertServerPathToClient, mapTextToErrors } from '../../../service/ErrorsMapper/ErrorsMapper';
import { BannerId } from '../../../typings/banners';
import { ErrorsEdit, PageStatus, ValidationResultItem } from '../../../typings/common';
import { EditInternalBannersState } from '../../../typings/internalBannersEditState';

function fillDraftWithInternalBannerErrors(
    draft: Draft<EditInternalBannersState>,
    errors: ValidationResultItem[],
    ids: BannerId[]
): void {
    const clientValidationItems = convertServerPathToClient(errors, internalBannerFieldErrorMapper);
    const errorsClientWithText = mapTextToErrors(clientValidationItems, internalBannersErrorsMapper);

    const { banners: bannersErrorTree } = buildErrorTree<Partial<ErrorsEdit>>(errorsClientWithText);

    if (bannersErrorTree) {
        Object.keys(bannersErrorTree).forEach(key => {
            const bannerId = ids[key];
            const errorsTree = bannersErrorTree[key];

            if (errorsTree) {
                set(draft.banners[bannerId], 'errors', errorsTree);
            }
        });
    }
}

export function saveInternalBannersSuccess(
    state: EditInternalBannersState,
    action: SaveInternalBannersSuccess
): EditInternalBannersState {
    return produce(state, draft => {
        const { updated, added, deleted } = action.result;

        if (updated.banners.length) {
            updated.banners.forEach(banner => {
                if (banner) {
                    draft.banners[banner.id].isSaved = true;
                }
            });
        }

        if (added.banners.length) {
            added.banners.forEach((banner, index) => {
                if (!banner) {
                    return;
                }

                const oldId = added.ids[index];
                // TODO: https://st.yandex-team.ru/DIRECT-98219
                const newId = String(banner.id);

                draft.banners[newId] = {
                    ...draft.banners[oldId],
                    id: newId,
                    isNew: false,
                    isSaved: true
                };

                draft.bannersDraft[newId] = {
                    ...draft.bannersDraft[oldId]
                };

                const groupId = draft.banners[newId].group.id;
                const groupBannersIds = draft.groupBannersList[groupId].bannersIds;
                const bannerIdIndex = groupBannersIds.indexOf(oldId);

                if (bannerIdIndex > -1) {
                    groupBannersIds.splice(bannerIdIndex, 1, newId);
                }

                delete draft.banners[oldId];
                delete draft.bannersDraft[oldId];

                draft.bannersIds.push(newId);
            });
        }

        if (deleted.ids.length) {
            deleted.ids.forEach(id => {
                if (id) {
                    draft.banners[id].isEditAllow = false;
                }
            });
        }

        if (updated.errors.length) {
            fillDraftWithInternalBannerErrors(draft, updated.errors, updated.ids);
        }

        if (added.errors.length) {
            fillDraftWithInternalBannerErrors(draft, added.errors, added.ids);
        }

        draft.pageStatus = PageStatus.LOADED;
    });
}
